import { resolveChecklistMediaUri } from './resolveChecklistMediaUri';
import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';

export type ChecklistPhotoDownloadResult = {
  status: 'saved' | 'shared' | 'cancelled';
  fileName?: string;
};

type PhotoFileInfo = {
  extension: 'jpg' | 'png' | 'webp';
  mimeType: 'image/jpeg' | 'image/png' | 'image/webp';
  uti: 'public.jpeg' | 'public.png' | 'org.webmproject.webp';
};

const sanitizeFileBaseName = (value: string): string => {
  const sanitized = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return sanitized || 'foto-checklist';
};

const resolvePhotoFileInfo = (uri: string, mimeType?: string | null): PhotoFileInfo => {
  const normalizedMimeType = mimeType?.toLowerCase();
  const normalizedUri = uri.toLowerCase().split(/[?#]/)[0];

  if (normalizedMimeType === 'image/png' || normalizedUri.endsWith('.png') || uri.startsWith('data:image/png')) {
    return { extension: 'png', mimeType: 'image/png', uti: 'public.png' };
  }

  if (normalizedMimeType === 'image/webp' || normalizedUri.endsWith('.webp') || uri.startsWith('data:image/webp')) {
    return { extension: 'webp', mimeType: 'image/webp', uti: 'org.webmproject.webp' };
  }

  return { extension: 'jpg', mimeType: 'image/jpeg', uti: 'public.jpeg' };
};

const readAndroidPhotoAsBase64 = async (
  uri: string,
  temporaryUri: string,
  fileSystem: typeof FileSystem
): Promise<{ base64: string; mimeType?: string | null; temporaryFileCreated: boolean }> => {
  if (uri.startsWith('data:')) {
    const separatorIndex = uri.indexOf(',');
    const metadata = separatorIndex >= 0 ? uri.slice(0, separatorIndex) : '';
    const base64 = separatorIndex >= 0 ? uri.slice(separatorIndex + 1) : '';
    if (!metadata.includes(';base64') || !base64) {
      throw new Error('Formato de imagem local não suportado para download.');
    }
    return {
      base64,
      mimeType: metadata.match(/^data:([^;]+)/)?.[1] ?? null,
      temporaryFileCreated: false,
    };
  }

  let sourceUri = uri;
  let temporaryFileCreated = false;
  let mimeType: string | null = null;

  if (/^https?:\/\//i.test(uri)) {
    const download = await fileSystem.downloadAsync(uri, temporaryUri);
    if (download.status < 200 || download.status >= 300) {
      throw new Error(`Falha ao baixar a foto (${download.status}).`);
    }
    sourceUri = download.uri;
    temporaryFileCreated = true;
    mimeType = download.headers?.['content-type'] ?? null;
  }

  try {
    return {
      base64: await fileSystem.readAsStringAsync(sourceUri, {
        encoding: fileSystem.EncodingType.Base64,
      }),
      mimeType,
      temporaryFileCreated,
    };
  } catch (error) {
    if (temporaryFileCreated) {
      await fileSystem.deleteAsync(temporaryUri, { idempotent: true }).catch(() => undefined);
    }
    throw error;
  }
};

export const downloadChecklistPhoto = async (
  uri: string,
  fileBaseName: string,
  environment: {
    platform?: string;
    fileSystem?: typeof FileSystem;
    sharing?: Pick<typeof Sharing, 'isAvailableAsync' | 'shareAsync'>;
  } = {}
): Promise<ChecklistPhotoDownloadResult> => {
  uri = await resolveChecklistMediaUri(uri);
  const safeBaseName = sanitizeFileBaseName(fileBaseName);
  const platform = environment.platform ?? Platform.OS;
  const fileSystem = environment.fileSystem ?? FileSystem;
  const sharing = environment.sharing ?? Sharing;

  if (platform === 'web') {
    const response = await fetch(uri);
    if (!response.ok) {
      throw new Error(`Falha ao baixar a foto (${response.status}).`);
    }

    const blob = await response.blob();
    const fileInfo = resolvePhotoFileInfo(uri, blob.type);
    const fileName = `${safeBaseName}.${fileInfo.extension}`;
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);

    return { status: 'saved', fileName };
  }

  if (platform === 'android') {
    const initialDirectory = fileSystem.StorageAccessFramework.getUriForDirectoryInRoot('Documents');
    const permission = await fileSystem.StorageAccessFramework.requestDirectoryPermissionsAsync(initialDirectory);
    if (!permission.granted) {
      return { status: 'cancelled' };
    }

    const temporaryUri = `${fileSystem.cacheDirectory ?? fileSystem.documentDirectory}${safeBaseName}-download`;
    let temporaryFileCreated = false;

    try {
      const source = await readAndroidPhotoAsBase64(uri, temporaryUri, fileSystem);
      temporaryFileCreated = source.temporaryFileCreated;
      const fileInfo = resolvePhotoFileInfo(uri, source.mimeType);
      const destinationUri = await fileSystem.StorageAccessFramework.createFileAsync(
        permission.directoryUri,
        safeBaseName,
        fileInfo.mimeType
      );

      await fileSystem.StorageAccessFramework.writeAsStringAsync(destinationUri, source.base64, {
        encoding: fileSystem.EncodingType.Base64,
      });

      return {
        status: 'saved',
        fileName: `${safeBaseName}.${fileInfo.extension}`,
      };
    } finally {
      if (temporaryFileCreated) {
        await fileSystem.deleteAsync(temporaryUri, { idempotent: true }).catch(() => undefined);
      }
    }
  }

  if (platform === 'ios') {
    if (!await sharing.isAvailableAsync()) {
      throw new Error('Compartilhamento de arquivos não disponível neste dispositivo.');
    }

    const cacheDirectory = fileSystem.cacheDirectory;
    if (!cacheDirectory) {
      throw new Error('Diretório temporário não disponível para compartilhar a foto.');
    }

    const fileInfo = resolvePhotoFileInfo(uri);
    const fileName = `${safeBaseName}.${fileInfo.extension}`;
    const localUri = `${cacheDirectory}${fileName}`;

    if (/^https?:\/\//i.test(uri)) {
      const download = await fileSystem.downloadAsync(uri, localUri);
      if (download.status < 200 || download.status >= 300) {
        throw new Error(`Falha ao baixar a foto (${download.status}).`);
      }
    } else if (uri.startsWith('data:')) {
      const separatorIndex = uri.indexOf(',');
      const metadata = separatorIndex >= 0 ? uri.slice(0, separatorIndex) : '';
      const base64 = separatorIndex >= 0 ? uri.slice(separatorIndex + 1) : '';
      if (!metadata.includes(';base64') || !base64) {
        throw new Error('Formato de imagem local não suportado para download.');
      }
      await fileSystem.writeAsStringAsync(localUri, base64, {
        encoding: fileSystem.EncodingType.Base64,
      });
    } else {
      await fileSystem.copyAsync({ from: uri, to: localUri });
    }

    await sharing.shareAsync(localUri, { UTI: fileInfo.uti });
    return { status: 'shared', fileName };
  }

  throw new Error('Download de foto não disponível nesta plataforma.');
};
